import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Chatbot from '../components/Chatbot';
import WhatsAppButton from '../components/WhatsAppButton';
import SocialButtons from '../components/SocialButtons';
import { 
  FileText, 
  Calculator, 
  Building2, 
  TrendingUp, 
  ClipboardCheck, 
  Search, 
  Briefcase, 
  FileCheck,
  Phone,
  Mail,
  MapPin,
  Star,
  CheckCircle2,
  Menu,
  X,
  ArrowRight,
  Shield,
  ChevronDown,
  Laptop,
  Factory,
  ShoppingCart,
  Heart,
  GraduationCap,
  Hotel,
  DollarSign,
  Truck,
  Sprout,
  Shirt,
  Ship,
  BookOpen,
  Facebook,
  Instagram,
  Clock,
  Upload,
  Briefcase as BriefcaseIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  firmInfo, 
  services, 
  testimonials, 
  aboutContent, 
  heroContent,
  industries,
  blogArticles
} from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  FileText,
  Calculator,
  Building2,
  TrendingUp,
  ClipboardCheck,
  Search,
  Briefcase,
  FileCheck,
  Building: Building2,
  Rocket: TrendingUp,
  BookOpen: FileText,
  UserCheck: CheckCircle2,
  Globe: MapPin,
  Laptop,
  Factory,
  ShoppingCart,
  Heart,
  GraduationCap,
  Hotel,
  DollarSign,
  Truck,
  Sprout,
  Shirt,
  Ship
};

// Testimonial Carousel Component
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative">
      {/* Navigation Arrows */}
      <button
        onClick={prevTestimonial}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:bg-teal-600 hover:text-white flex items-center justify-center group"
        aria-label="Previous testimonial"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        onClick={nextTestimonial}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:bg-teal-600 hover:text-white flex items-center justify-center group"
        aria-label="Next testimonial"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Testimonial Card */}
      <Card className="max-w-4xl mx-auto shadow-2xl border-2">
        <CardContent className="pt-12 pb-12 px-8 md:px-16">
          <div className="text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed italic">
              "{currentTestimonial.content}"
            </p>
            <div className="border-t pt-6 mx-auto max-w-md">
              <p className="font-bold text-xl text-gray-900">{currentTestimonial.name}</p>
              <p className="text-gray-600 mt-1">{currentTestimonial.designation}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentIndex ? 'bg-teal-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      
      if (response.data.success) {
        toast.success("Message Sent!", {
          description: response.data.message,
        });
        setContactForm({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error("Error", {
        description: error.response?.data?.detail || "Failed to send message. Please try again or contact us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Notification Ribbon */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-2 px-4 text-center text-sm fixed top-0 left-0 right-0 z-50">
        <div className="animate-scroll inline-block whitespace-nowrap">
          📢 <strong>Important Updates:</strong> GST Return Q4 Due: Jan 20 | Income Tax Advance Tax Q4: Mar 15 | Form 16 Issue: Jun 15 | ITR Filing Deadline: Jul 31 | Stay Compliant!
        </div>
      </div>

      {/* Top Contact Bar */}
      <div className="bg-gray-900 text-white py-2 px-4 fixed top-8 left-0 right-0 z-40">
        <div className="container mx-auto flex flex-wrap items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center gap-4">
            <a href={`tel:${firmInfo.phone}`} className="flex items-center gap-1 hover:text-teal-400">
              <Phone className="w-3 h-3" />
              <span>{firmInfo.phone}</span>
            </a>
            <a href={`mailto:${firmInfo.email}`} className="hidden sm:flex items-center gap-1 hover:text-teal-400">
              <Mail className="w-3 h-3" />
              <span>{firmInfo.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Clock className="w-3 h-3" />
            <span>Mon-Sat: 11AM-9PM</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="fixed top-16 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src={firmInfo.logoUrl} 
                alt="CA Ravi Sahu Logo" 
                className="h-14 w-auto object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{firmInfo.name}</h1>
                <p className="text-xs text-gray-600">Chartered Accountants</p>
              </div>
            </div>

            {/* Desktop Navigation - Always Visible */}
            <nav className="hidden lg:flex items-center space-x-6">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">About</button>
              
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesDropdownOpen(true)}
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <button className="flex items-center gap-1 text-gray-700 hover:text-teal-600 font-medium transition-colors">
                  Services
                  <ChevronDown className="w-4 h-4" />
                </button>
                {servicesDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    {services.filter(s => s.detailPage).slice(0, 8).map((service) => (
                      <button
                        key={service.id}
                        onClick={() => navigate(`/services/${service.slug}`)}
                        className="w-full text-left px-4 py-2 hover:bg-teal-50 hover:text-teal-600 transition-colors text-sm"
                      >
                        {service.title}
                      </button>
                    ))}
                    <div className="border-t my-2"></div>
                    <button
                      onClick={() => scrollToSection('services')}
                      className="w-full text-left px-4 py-2 hover:bg-teal-50 hover:text-teal-600 transition-colors text-sm font-semibold"
                    >
                      View All Services
                    </button>
                  </div>
                )}
              </div>
              
              <button onClick={() => navigate('/blog')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Blog</button>
              <button onClick={() => navigate('/ai-tools')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">AI Tools</button>
              <button onClick={() => navigate('/calculators')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Calculators</button>
              <button onClick={() => navigate('/compliance-calendar')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Compliance</button>
              <button onClick={() => navigate('/gallery')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Gallery</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Testimonials</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-600 font-medium transition-colors">Contact</button>
              <Button onClick={() => scrollToSection('contact')} className="bg-teal-600 hover:bg-teal-700 text-white">
                Get Started
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('home')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">About</button>
                <button onClick={() => scrollToSection('services')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Services</button>
                <button onClick={() => navigate('/blog')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Blog</button>
                <button onClick={() => navigate('/ai-tools')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">AI Tools</button>
                <button onClick={() => navigate('/calculators')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Calculators</button>
                <button onClick={() => navigate('/compliance-calendar')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Compliance</button>
                <button onClick={() => navigate('/gallery')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Gallery</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Testimonials</button>
                <button onClick={() => scrollToSection('contact')} className="text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">Contact</button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-40 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium border border-teal-200">
                  Certified ICAI Chartered Accountants
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {heroContent.title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {heroContent.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  size="lg" 
                  onClick={() => scrollToSection('contact')}
                  className="bg-teal-600 hover:bg-teal-700 text-white text-lg h-14 px-8"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-gray-300 hover:border-teal-600 hover:text-teal-600 text-lg h-14 px-8"
                >
                  Our Services
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-6">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-teal-600" />
                  <a href={`tel:${firmInfo.phone}`} className="text-gray-700 hover:text-teal-600 font-medium">
                    {firmInfo.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-teal-600" />
                  <a href={`mailto:${firmInfo.email}`} className="text-gray-700 hover:text-teal-600 font-medium">
                    {firmInfo.email}
                  </a>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={heroContent.image} 
                  alt="Professional Chartered Accountant Services"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-6 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">1000+</p>
                    <p className="text-sm text-gray-600">Satisfied Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={aboutContent.image} 
                alt="About Ravi Sahu and Associates"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-teal-600/10 to-transparent rounded-2xl"></div>
            </div>
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  {aboutContent.title}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {aboutContent.description}
                </p>
              </div>
              <div className="space-y-3">
                {aboutContent.credentials.map((credential, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{credential}</p>
                  </div>
                ))}
              </div>
              <Button 
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="bg-teal-600 hover:bg-teal-700 text-white mt-4"
              >
                Connect With Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive financial and compliance solutions tailored to your business needs
            </p>
          </div>

          {/* Featured Services */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {services.filter(s => s.featured).map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-teal-500 cursor-pointer" onClick={() => service.detailPage && navigate(`/services/${service.slug}`)}>
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-teal-600 transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed mb-4">
                      {service.description}
                    </CardDescription>
                    {service.detailPage && (
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-teal-600 font-semibold group-hover:gap-2 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/services/${service.slug}`);
                        }}
                      >
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:ml-2 transition-all" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional Services */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Additional Services</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.filter(s => !s.featured).map((service) => {
                const Icon = iconMap[service.icon];
                return (
                  <div 
                    key={service.id} 
                    className={`flex items-start gap-3 p-4 rounded-lg hover:bg-teal-50 transition-colors ${service.detailPage ? 'cursor-pointer' : ''}`}
                    onClick={() => service.detailPage && navigate(`/services/${service.slug}`)}
                  >
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{service.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      {service.detailPage && (
                        <button className="text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1">
                          View Details
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600">
              Providing specialized financial and compliance solutions across diverse sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry) => {
              const Icon = iconMap[industry.icon] || Building2;
              return (
                <Card key={industry.id} className="group hover:shadow-lg transition-all duration-300 hover:scale-105">
                  <CardContent className="pt-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:from-teal-500 group-hover:to-teal-600 transition-all">
                      <Icon className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm group-hover:text-teal-600 transition-colors">
                      {industry.name}
                    </h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog/Articles Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Latest Insights
            </h2>
            <p className="text-lg text-gray-600">
              Expert articles on taxation, compliance, and business strategies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {blogArticles.slice(0, 3).map((article) => (
              <Card
                key={article.id}
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => navigate(`/blog/${article.slug}`)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg group-hover:text-teal-600 transition-colors line-clamp-2">
                    {article.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {article.excerpt}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>{article.readTime}</span>
                    <Button variant="link" className="p-0 h-auto text-teal-600">
                      Read More <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              onClick={() => navigate('/blog')}
              variant="outline"
              className="border-2 border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white"
            >
              View All Articles
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Carousel */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Client Testimonials
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by businesses and individuals across Madhya Pradesh
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative max-w-6xl mx-auto">
            <TestimonialCarousel />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-lg text-gray-600">
              Have a question or need assistance? We're here to help.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Phone</p>
                    <a href={`tel:${firmInfo.phone}`} className="text-gray-600 hover:text-teal-600">
                      {firmInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Email</p>
                    <a href={`mailto:${firmInfo.email}`} className="text-gray-600 hover:text-teal-600 break-all">
                      {firmInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Office Address</p>
                    <p className="text-gray-600">
                      {firmInfo.address}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl shadow-lg p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Office Hours</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5" />
                    <span className="font-semibold">Visit us during:</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Monday - Saturday</span>
                    <span className="font-semibold">11:00 AM - 9:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                </div>
                
                {/* Social Media Links */}
                <div className="mt-6 pt-6 border-t border-teal-500">
                  <h4 className="font-semibold mb-3">Connect With Us</h4>
                  <div className="flex gap-3">
                    <a 
                      href={firmInfo.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a 
                      href={firmInfo.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <iframe
                  src={firmInfo.googleMapsEmbed}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    className="w-full"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-lg"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block p-3 bg-teal-100 rounded-full mb-4">
                <BriefcaseIcon className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Join Our Team
              </h2>
              <p className="text-lg text-gray-600">
                Be part of a growing team of professionals making a difference in the world of finance and compliance
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">We're Hiring!</h3>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Growth Opportunities</h4>
                    <p className="text-gray-600">Work with diverse clients and expand your expertise across taxation, audit, and compliance</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Learning Environment</h4>
                    <p className="text-gray-600">Continuous learning with exposure to latest regulations and best practices</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Collaborative Team</h4>
                    <p className="text-gray-600">Work alongside experienced CA, CS, and legal professionals</p>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Current Openings:</h4>
                <ul className="space-y-2 mb-6">
                  <li className="text-gray-700">• Chartered Accountant (CA) - 1-3 years experience</li>
                  <li className="text-gray-700">• Article Assistant for CA</li>
                  <li className="text-gray-700">• Tax Consultant - GST & Income Tax</li>
                  <li className="text-gray-700">• Audit Senior</li>
                </ul>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href={`mailto:${firmInfo.email}?subject=Job Application - [Position Name]&body=Dear Sir,%0D%0A%0D%0AI am interested in applying for the position at Ravi Sahu and Associates.%0D%0A%0D%0APlease find my resume attached.`}
                    className="flex-1"
                  >
                    <Button className="w-full bg-teal-600 hover:bg-teal-700">
                      <Mail className="w-4 h-4 mr-2" />
                      Email Your Resume
                    </Button>
                  </a>
                  <a href={`tel:${firmInfo.phone}`} className="flex-1">
                    <Button variant="outline" className="w-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50">
                      <Phone className="w-4 h-4 mr-2" />
                      Call Us
                    </Button>
                  </a>
                </div>
                
                <p className="text-sm text-gray-600 mt-4">
                  Send your resume to <span className="font-semibold text-teal-600">{firmInfo.email}</span> with the subject line mentioning the position you're applying for.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{firmInfo.name}</h3>
                  <p className="text-xs text-gray-400">Chartered Accountants</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                {firmInfo.tagline}
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-teal-400 text-sm">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-teal-400 text-sm">About Us</button>
                <button onClick={() => scrollToSection('services')} className="block text-gray-400 hover:text-teal-400 text-sm">Services</button>
                <button onClick={() => scrollToSection('testimonials')} className="block text-gray-400 hover:text-teal-400 text-sm">Testimonials</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-teal-400 text-sm">Contact</button>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <div className="space-y-3 text-sm">
                <p className="text-gray-400">{firmInfo.address}</p>
                <p className="text-gray-400">{firmInfo.phone}</p>
                <p className="text-gray-400">{firmInfo.email}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <p className="text-center text-gray-400 text-sm">
              © {new Date().getFullYear()} {firmInfo.name}. All rights reserved. | Team of CA, CS and Lawyers
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Components */}
      <WhatsAppButton />
      <SocialButtons />
      <Chatbot type="general" />
      <Chatbot type="tax_advisory" />
    </div>
  );
};

export default Home;
